import React, { useState, useEffect } from 'react';
import { OrgSnapshot } from '../components/OrgSnapshot';
import * as api from '../api/client';

interface TrendPoint {
  date: string;      // YYYY-MM-DD
  average: number;   // 0..1
  consultants: number;
  issues: number;
}

/**
 * Company average over the last 6 months, from the daily snapshots. History
 * only exists from when the daily capture started (~20 Jul 2026) and can't be
 * backfilled, so the line fills toward 6 months as days accrue.
 */
function CompanyTrend() {
  const [points, setPoints] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.getCompanyTrend(180)
      .then(res => { if (!cancelled) setPoints(res.points || []); })
      .catch(() => { /* leave empty */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const shortDate = (d: string) => {
    const [, m, day] = d.split('-');
    return `${Number(day)}/${Number(m)}`;
  };

  if (loading) {
    return (
      <div className="card" style={{ marginBottom: '24px', textAlign: 'center', padding: '20px' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Loading trend…</span>
      </div>
    );
  }

  if (points.length === 0) {
    return (
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">Company average — last 6 months</div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '10px 0 0' }}>
          No history yet — the company average is recorded once a day, so the line builds up from here.
        </p>
      </div>
    );
  }

  const W = 860, H = 220;
  const PAD = { t: 16, r: 20, b: 28, l: 40 };
  const plotW = W - PAD.l - PAD.r;
  const plotH = H - PAD.t - PAD.b;
  const x = (i: number) => PAD.l + (points.length === 1 ? plotW / 2 : (i * plotW) / (points.length - 1));
  const y = (v: number) => PAD.t + (1 - Math.max(0, Math.min(1, v))) * plotH;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.average).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${(PAD.t + plotH).toFixed(1)} L${x(0).toFixed(1)},${(PAD.t + plotH).toFixed(1)} Z`;

  const first = points[0];
  const last = points[points.length - 1];
  const changePts = Math.round((last.average - first.average) * 1000) / 10;
  const active = hoverIdx !== null ? points[hoverIdx] : null;

  return (
    <div className="card" style={{ marginBottom: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '4px' }}>
        <div className="card-header" style={{ marginBottom: 0 }}>Company average — last 6 months</div>
        {points.length > 1 && (
          <span style={{ fontSize: '12px', color: changePts >= 0 ? 'var(--state-positive)' : 'var(--state-warning)' }}>
            {changePts >= 0 ? '▲' : '▼'} {Math.abs(changePts)} pts since {shortDate(first.date)}
          </span>
        )}
      </div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setHoverIdx(null)}
        onMouseMove={e => {
          const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
          const px = ((e.clientX - rect.left) / rect.width) * W;
          const i = Math.round(((px - PAD.l) / plotW) * (points.length - 1));
          setHoverIdx(Math.max(0, Math.min(points.length - 1, i)));
        }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map(v => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} stroke="var(--border-card)" strokeWidth="1" />
            <text x={PAD.l - 8} y={y(v) + 4} textAnchor="end" fontSize="10" fill="var(--text-secondary)">
              {Math.round(v * 100)}%
            </text>
          </g>
        ))}

        <path d={area} fill="var(--accent-teal)" opacity="0.12" />
        <path d={line} fill="none" stroke="var(--accent-teal)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        <circle cx={x(points.length - 1)} cy={y(last.average)} r="4.5" fill="var(--accent-teal)" stroke="var(--bg-card)" strokeWidth="2" />
        <text x={x(points.length - 1)} y={y(last.average) - 10} textAnchor="end" fontSize="11" fontWeight="500" fill="var(--text-primary)">
          {Math.round(last.average * 100)}%
        </text>

        <text x={PAD.l} y={H - 8} fontSize="10" fill="var(--text-secondary)">{shortDate(first.date)}</text>
        {points.length > 1 && (
          <text x={W - PAD.r} y={H - 8} textAnchor="end" fontSize="10" fill="var(--text-secondary)">{shortDate(last.date)}</text>
        )}

        {active && hoverIdx !== null && (
          <g pointerEvents="none">
            <line x1={x(hoverIdx)} x2={x(hoverIdx)} y1={PAD.t} y2={PAD.t + plotH} stroke="var(--text-secondary)" strokeWidth="1" opacity="0.5" />
            <circle cx={x(hoverIdx)} cy={y(active.average)} r="5" fill="var(--accent-teal)" stroke="var(--bg-card)" strokeWidth="2" />
            <g transform={`translate(${Math.min(Math.max(x(hoverIdx), PAD.l + 60), W - PAD.r - 60)}, ${PAD.t + 8})`}>
              <rect x="-60" y="-2" width="120" height="34" rx="6" fill="var(--bg-page)" stroke="var(--border-card)" />
              <text x="0" y="12" textAnchor="middle" fontSize="10" fill="var(--text-secondary)">{shortDate(active.date)}</text>
              <text x="0" y="26" textAnchor="middle" fontSize="12" fontWeight="500" fill="var(--text-primary)">
                {Math.round(active.average * 100)}% · {active.consultants} scored
              </text>
            </g>
          </g>
        )}
      </svg>
    </div>
  );
}

export function CompanyView() {
  return (
    <div>
      <h1 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '24px' }}>Company</h1>
      <CompanyTrend />
      <OrgSnapshot />
    </div>
  );
}
